/**
 * Maps the Rails crm-api validation payload (`errors: string[]` — the
 * `@invoice.errors.full_messages` array returned alongside HTTP 422) onto
 * the form's existing `validationErrors` surface.
 *
 * The form already renders `validationErrors.general` as a persistent red
 * banner and field-level messages for customer/products. Server validation
 * failures used to bypass that and go to an ephemeral toast (create joined
 * them in a toast that auto-dismissed; edit showed only `errors[0]`), which
 * is why they "never appeared on screen". This helper is the single place
 * that turns the server array into a durable banner string plus a
 * field-anchored message for anything about the due date.
 */

const DUE_DATE_RE = /due date/i;

const FALLBACK = "Something went wrong saving this invoice. Please review the fields and try again.";

export interface SplitInvoiceErrors {
  /** Always non-empty — drives the persistent banner (`whitespace-pre-line`). */
  general: string;
  /** Set only when a message concerns the due date — anchors to that field. */
  dueDate?: string;
}

export function splitInvoiceServerErrors(errors: readonly string[]): SplitInvoiceErrors {
  const cleaned = (errors ?? [])
    .map((e) => (typeof e === "string" ? e.trim() : ""))
    .filter((e) => e.length > 0);

  if (cleaned.length === 0) {
    return { general: FALLBACK };
  }

  const general =
    cleaned.length === 1
      ? (cleaned[0] as string)
      : cleaned.map((e) => `• ${e}`).join("\n");

  const dueDateMessages = cleaned.filter((e) => DUE_DATE_RE.test(e));
  const dueDate = dueDateMessages.length > 0 ? dueDateMessages.join("\n") : undefined;

  return dueDate !== undefined ? { general, dueDate } : { general };
}
